import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import FloatingPortal from './FloatingPortal';

interface AutocompleteProps<T> {
    label: string;
    placeholder: string;
    value: T | null;
    onSelect: (item: T | null) => void;
    onSearch: (searchText: string) => Promise<T[]>;
    displayField: keyof T;
    idField: keyof T;
    minSearchLength?: number;
    required?: boolean;
    disabled?: boolean;
    error?: string;
}

/**
 * Autocomplete Component
 * A beautiful, accessible autocomplete/search dropdown with debouncing
 */
export default function Autocomplete<T>({
    label,
    placeholder,
    value,
    onSelect,
    onSearch,
    displayField,
    idField,
    minSearchLength = 3,
    required = false,
    disabled = false,
    error,
}: AutocompleteProps<T>) {
    const [searchText, setSearchText] = useState('');
    const [results, setResults] = useState<T[]>([]);
    const [isOpen, setIsOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [highlightedIndex, setHighlightedIndex] = useState(-1);

    const anchorRef = useRef<HTMLDivElement>(null);
    const panelRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

    // Display selected value in input when not searching
    useEffect(() => {
        if (value && !isOpen) {
            setSearchText(String(value[displayField]));
        }
    }, [value, displayField, isOpen]);

    // Debounced search function
    const debouncedSearch = useCallback(
        (text: string) => {
            // Clear existing timer
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }

            // If text is too short, clear results
            if (text.length < minSearchLength) {
                setResults([]);
                setIsLoading(false);
                return;
            }

            // Set loading state
            setIsLoading(true);

            // Create new debounce timer (500ms)
            debounceTimerRef.current = setTimeout(async () => {
                try {
                    const searchResults = await onSearch(text);
                    setResults(searchResults);
                    setIsOpen(true);
                } catch (err) {
                    console.error('Search error:', err);
                    setResults([]);
                } finally {
                    setIsLoading(false);
                }
            }, 500);
        },
        [onSearch, minSearchLength]
    );

    // Handle input change
    const handleInputChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const text = e.target.value;
            setSearchText(text);
            setHighlightedIndex(-1);

            // Trigger debounced search
            debouncedSearch(text);
        },
        [debouncedSearch]
    );

    // Handle item selection
    const handleSelect = useCallback(
        (item: T) => {
            setSearchText(String(item[displayField]));
            setIsOpen(false);
            setResults([]);
            onSelect(item); // Call onSelect after setting internal state
            inputRef.current?.blur();
        },
        [onSelect, displayField]
    );

    // Handle input focus
    const handleFocus = useCallback(() => {
        if (value) {
            setSearchText('');
        }
        if (searchText.length >= minSearchLength && results.length > 0) {
            setIsOpen(true);
        }
    }, [value, searchText, minSearchLength, results.length]);

    // Handle clear button
    const handleClear = useCallback(
        (e: React.MouseEvent) => {
            e.stopPropagation();
            setSearchText('');
            setResults([]);
            setIsOpen(false);
            onSelect(null); // Call onSelect after setting internal state
            inputRef.current?.focus();
        },
        [onSelect]
    );

    // Handle keyboard navigation
    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent) => {
            if (!isOpen) return;

            switch (e.key) {
                case 'ArrowDown':
                    e.preventDefault();
                    setHighlightedIndex((prev) => (prev < results.length - 1 ? prev + 1 : prev));
                    break;
                case 'ArrowUp':
                    e.preventDefault();
                    setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : -1));
                    break;
                case 'Enter':
                    e.preventDefault();
                    if (highlightedIndex >= 0 && results[highlightedIndex]) {
                        handleSelect(results[highlightedIndex]);
                    }
                    break;
                case 'Escape':
                    e.preventDefault();
                    setIsOpen(false);
                    inputRef.current?.blur();
                    break;
            }
        },
        [isOpen, results, highlightedIndex, handleSelect]
    );

    // Click outside to close
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as Node;
            if (anchorRef.current?.contains(target) || panelRef.current?.contains(target)) {
                return;
            }
            setIsOpen(false);
            if (value) {
                setSearchText(String(value[displayField]));
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [value, displayField]);

    // Cleanup debounce timer on unmount
    useEffect(() => {
        return () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
        };
    }, []);

    // Show hint based on search text length
    const showHint = useMemo(
        () => searchText.length > 0 && searchText.length < minSearchLength,
        [searchText, minSearchLength]
    );

    const showNoResults = useMemo(
        () => !isLoading && searchText.length >= minSearchLength && results.length === 0 && isOpen,
        [isLoading, searchText, minSearchLength, results.length, isOpen]
    );

    return (
        <div className="relative space-y-2">
            {/* Label */}
            <label className="block text-sm font-medium text-[#1e293b]">
                {label}
                {required && <span className="text-[#ef4444] ml-1">*</span>}
            </label>

            {/* Input Container */}
            <div ref={anchorRef} className="relative">
                {/* Search Icon */}
                <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none">
                    <svg
                        className="w-5 h-5 text-[#64748b]"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                        />
                    </svg>
                </div>

                {/* Input */}
                <input
                    ref={inputRef}
                    type="text"
                    value={searchText}
                    onChange={handleInputChange}
                    onFocus={handleFocus}
                    onKeyDown={handleKeyDown}
                    disabled={disabled}
                    placeholder={placeholder}
                    className={`w-full pl-10 pr-10 py-2.5 bg-white border-2 rounded-lg text-[#1e293b] transition-all focus:outline-none disabled:bg-[#f8fafc] disabled:cursor-not-allowed ${
                        error
                            ? 'border-[#ef4444] focus:border-[#ef4444] focus:ring-4 focus:ring-[#ef4444]/10'
                            : isOpen
                              ? 'border-[#6e64f0] ring-4 ring-[#6e64f0]/10'
                              : 'border-[#e2e8f0] focus:border-[#6e64f0] focus:ring-4 focus:ring-[#6e64f0]/10'
                    }`}
                />

                {/* Loading Spinner or Clear Button */}
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    {isLoading ? (
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-[#6e64f0]"></div>
                    ) : searchText && !disabled ? (
                        <button
                            type="button"
                            onClick={handleClear}
                            className="text-[#64748b] hover:text-[#ef4444] transition-colors"
                        >
                            <svg
                                className="w-5 h-5"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M6 18L18 6M6 6l12 12"
                                />
                            </svg>
                        </button>
                    ) : null}
                </div>
            </div>

            {/* Error Message */}
            {error && <p className="text-sm text-[#ef4444] flex items-center gap-1">{error}</p>}

            {/* Hint */}
            {showHint && (
                <p className="text-sm text-[#64748b]">
                    Type at least {minSearchLength} characters to search
                </p>
            )}

            {/* Dropdown */}
            <FloatingPortal
                anchorRef={anchorRef}
                panelRef={panelRef}
                isOpen={isOpen}
                maxHeight={300}
                className="bg-white shadow-xl border border-[#e2e8f0] rounded-lg overflow-y-auto"
            >
                {showNoResults ? (
                    <div className="p-4 text-center">
                        <svg
                            className="w-12 h-12 text-[#cbd5e1] mx-auto mb-2"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                            />
                        </svg>
                        <p className="text-[#64748b] font-medium">No results found</p>
                        <p className="text-sm text-[#94a3b8] mt-1">Try adjusting your search</p>
                    </div>
                ) : (
                    <div className="py-2">
                        {results.map((item, index) => {
                            const isSelected = value && value[idField] === item[idField];
                            const isHighlighted = index === highlightedIndex;

                            return (
                                <button
                                    key={String(item[idField])}
                                    type="button"
                                    onClick={() => handleSelect(item)}
                                    className={`w-full px-4 py-3 text-left transition-colors flex items-center justify-between ${
                                        isHighlighted || isSelected
                                            ? 'bg-[#f5f3ff] text-[#6e64f0]'
                                            : 'text-[#1e293b] hover:bg-[#f8fafc]'
                                    }`}
                                >
                                    <span className="font-medium">
                                        {String(item[displayField])}
                                    </span>
                                    {isSelected && (
                                        <svg
                                            className="w-5 h-5 text-[#6e64f0]"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M5 13l4 4L19 7"
                                            />
                                        </svg>
                                    )}
                                </button>
                            );
                        })}
                    </div>
                )}
            </FloatingPortal>
        </div>
    );
}
